import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Factory, ScanBarcode, Trash2, Loader2, Check, ChevronsUpDown, PackagePlus, FileBarChart,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  getRateOptions, validateBundle, createJobOrder,
} from '@/services/inhouseJobOrderApi';
import UsnStickerPrinter from '@/components/print/UsnStickerPrinter';
import { Checkbox } from '@/components/ui/checkbox';
import { JobberPicker } from '@/components/jobbers/JobberPicker';

import { EwbDocPanel } from '@/components/ewayBill/EwbDocPanel';
const CreateJobOrder = () => {
  const navigate = useNavigate();
  const bundleRef = useRef(null);

  // Jobber
  const [jobber, setJobber] = useState('');

  // Target item cascade
  const [designOpen, setDesignOpen] = useState(false);
  const [designQuery, setDesignQuery] = useState('');
  const [designOptions, setDesignOptions] = useState([]);
  const [design, setDesign] = useState('');
  const [shades, setShades] = useState([]);
  const [shade, setShade] = useState('');
  const [sizes, setSizes] = useState([]);
  const [size, setSize] = useState('');
  const [descriptions, setDescriptions] = useState([]);
  const [description, setDescription] = useState('');
  const [resolved, setResolved] = useState(null);

  // Bundles
  const [bundleValue, setBundleValue] = useState('');
  const [scanning, setScanning] = useState(false);
  const [bundles, setBundles] = useState([]);

  const [remarks, setRemarks] = useState('');
  const [skipPressing, setSkipPressing] = useState(false);
  const [submitting, setSubmitting] = useState(false);


  // Debounced design typeahead (server-side, 24k designs)
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const d = await getRateOptions({ q: designQuery });
        setDesignOptions(d.designs || []);
      } catch { setDesignOptions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [designQuery]);

  const pickDesign = async (d) => {
    setDesign(d); setDesignOpen(false);
    setShade(''); setSize(''); setDescription('');
    setShades([]); setSizes([]); setDescriptions([]); setResolved(null);
    try {
      const r = await getRateOptions({ design: d });
      setShades(r.shades || []);
      setResolved(r.resolved || null);
    } catch { /* noop */ }
  };

  const pickShade = async (s) => {
    setShade(s); setSize(''); setDescription('');
    setSizes([]); setDescriptions([]); setResolved(null);
    try {
      const r = await getRateOptions({ design, shade: s });
      setSizes(r.sizes || []);
      setResolved(r.resolved || null);
    } catch { /* noop */ }
  };

  const pickSize = async (sz) => {
    setSize(sz); setDescription(''); setDescriptions([]); setResolved(null);
    try {
      const r = await getRateOptions({ design, shade, size: sz });
      setDescriptions(r.descriptions || []);
      setResolved(r.resolved || null);
    } catch { /* noop */ }
  };

  const pickDescription = async (ds) => {
    setDescription(ds);
    try {
      const r = await getRateOptions({ design, shade, size, description: ds });
      setResolved(r.resolved || null);
    } catch { /* noop */ }
  };

  const totalQty = bundles.reduce((a, b) => a + (b.qty || 0), 0);

  const scanBundle = async (e) => {
    e?.preventDefault?.();
    const code = bundleValue.trim();
    if (!code) return;
    if (bundles.some((b) => b.bundle_code === code)) {
      toast.error(`${code} already added`);
      setBundleValue('');
      return;
    }
    setScanning(true);
    try {
      const expectedSort = bundles[0]?.sort_no || '';
      const r = await validateBundle(code, expectedSort);
      if (!r.valid) {
        toast.error(r.message || 'Invalid bundle');
      } else {
        setBundles((prev) => [...prev, r.bundle]);
        toast.success(`Added ${r.bundle.bundle_code} (${r.bundle.qty} pcs)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bundle validation failed');
    } finally {
      setScanning(false);
      setBundleValue('');
      setTimeout(() => bundleRef.current?.focus(), 40);
    }
  };

  const removeBundle = (code) => setBundles((prev) => prev.filter((b) => b.bundle_code !== code));

  const canSubmit = jobber && design && bundles.length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        jobber_name: jobber,
        design_name: design,
        shade, size, description,
        sku_code: resolved?.sku_code || '',
        bundle_codes: bundles.map((b) => b.bundle_code),
        remarks: remarks.trim(),
        skip_pressing: skipPressing,
      };
      const res = await createJobOrder(payload);
      toast.success(`Job order ${res.jw_id} raised — ${res.sent_qty} pcs sent`);
      navigate('/sections/inhouse/jobber-report');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create job order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="create-job-order-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/sections/inhouse')}
              className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <PackagePlus className="w-7 h-7" />
              <div>
                <h1 className="text-xl font-bold">Create Job Order</h1>
                <p className="text-sm text-[#E8DCC8]">Group Regular (Processed) or Job-Worker (Ready) bundles — same sort only</p>
              </div>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/sections/inhouse/jobber-report')}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="go-report-btn">
            <FileBarChart className="w-4 h-4 mr-2" /> Report
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 grid md:grid-cols-2 gap-4">
        {/* LEFT — Jobber + Target Item */}
        <div className="space-y-4">
          <Card className="border-indigo-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Factory className="w-5 h-5 text-indigo-600" /> Jobber
              </CardTitle>
            </CardHeader>
            <CardContent>
              <JobberPicker value={jobber} onChange={setJobber} testId="jobber" />
            </CardContent>
          </Card>

          <Card className="border-indigo-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Target Item <span className="text-xs font-normal text-gray-400">(from rates master)</span></CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Design */}
              <div>
                <Label className="text-xs text-gray-500">Design</Label>
                <Popover open={designOpen} onOpenChange={setDesignOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox"
                      className="w-full justify-between font-normal mt-1" data-testid="design-trigger">
                      <span className={`truncate ${design ? '' : 'text-gray-400'}`}>{design || 'Search design…'}</span>
                      <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput placeholder="Type to search 24k designs…" value={designQuery}
                        onValueChange={setDesignQuery} data-testid="design-search" />
                      <CommandList>
                        <CommandEmpty>Type to search designs…</CommandEmpty>
                        {designOptions.map((d) => (
                          <CommandItem key={d} value={d} onSelect={() => pickDesign(d)} data-testid={`design-option-${d}`}>
                            <Check className={`w-4 h-4 mr-2 shrink-0 ${design === d ? 'opacity-100' : 'opacity-0'}`} />
                            <span className="truncate">{d}</span>
                          </CommandItem>
                        ))}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Shade */}
              <div>
                <Label className="text-xs text-gray-500">Shade</Label>
                <Select value={shade} onValueChange={pickShade} disabled={!design || shades.length === 0}>
                  <SelectTrigger className="mt-1" data-testid="shade-trigger"><SelectValue placeholder={design ? 'Select shade' : 'Pick a design first'} /></SelectTrigger>
                  <SelectContent>
                    {shades.map((s) => <SelectItem key={s} value={s} data-testid={`shade-option-${s}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Size */}
              <div>
                <Label className="text-xs text-gray-500">Size</Label>
                <Select value={size} onValueChange={pickSize} disabled={!shade || sizes.length === 0}>
                  <SelectTrigger className="mt-1" data-testid="size-trigger"><SelectValue placeholder={shade ? 'Select size' : 'Pick a shade first'} /></SelectTrigger>
                  <SelectContent>
                    {sizes.map((s) => <SelectItem key={s} value={s} data-testid={`size-option-${s}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div>
                <Label className="text-xs text-gray-500">Description</Label>
                <Select value={description} onValueChange={pickDescription} disabled={!size || descriptions.length === 0}>
                  <SelectTrigger className="mt-1" data-testid="description-trigger"><SelectValue placeholder={size ? 'Select description' : 'Pick a size first'} /></SelectTrigger>
                  <SelectContent>
                    {descriptions.map((d) => <SelectItem key={d} value={d} data-testid={`description-option-${d}`}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {resolved?.sku_code && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm" data-testid="resolved-sku">
                  <span className="text-emerald-700">Resolved SKU: </span>
                  <span className="font-mono font-semibold text-emerald-800">{resolved.sku_code}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — Bundles + Submit */}
        <div className="space-y-4">
          <Card className="border-indigo-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ScanBarcode className="w-5 h-5 text-indigo-600" /> Scan Bundles
              </CardTitle>
              <p className="text-xs text-gray-500">Scan Regular (Processed) or Job-Worker (Ready) bundles — all must share the same Sort No (one supplier)</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <form onSubmit={scanBundle} className="flex gap-2">
                <Input ref={bundleRef} value={bundleValue} onChange={(e) => setBundleValue(e.target.value)}
                  placeholder="Scan bundle code…" autoFocus disabled={scanning} data-testid="bundle-scan-input" />
                <Button type="submit" disabled={scanning || !bundleValue.trim()} data-testid="bundle-scan-btn">
                  {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                </Button>
              </form>

              {bundles.length > 0 && bundles[0]?.sort_no && (
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <span className="text-xs text-amber-700">Sort No locked (one supplier)</span>
                  <span className="font-mono font-semibold text-amber-800 text-sm" data-testid="order-sort-no">{bundles[0].sort_no}</span>
                </div>
              )}

              <div className="flex items-center justify-between bg-indigo-50 rounded-lg px-3 py-2">
                <span className="text-sm text-indigo-700">Sent Qty ({bundles.length} bundles)</span>
                <span className="text-2xl font-bold text-indigo-800" data-testid="total-qty">{totalQty}</span>
              </div>

              <div className="border rounded-lg divide-y max-h-72 overflow-y-auto" data-testid="bundle-list">
                {bundles.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6" data-testid="bundle-empty">No bundles yet</p>
                ) : bundles.map((b) => (
                  <div key={b.bundle_code} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`bundle-row-${b.bundle_code}`}>
                    <div className="min-w-0">
                      <p className="font-mono text-gray-800 truncate">{b.bundle_code}</p>
                      <p className="text-[11px] text-gray-500 truncate">
                        {b.design_name || '—'}{b.shade ? ` · ${b.shade}` : ''}{b.sort_no ? ` · Sort ${b.sort_no}` : ''}{b.flow_type ? ` · ${b.flow_type}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-semibold text-indigo-700">{b.qty}</span>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                        onClick={() => removeBundle(b.bundle_code)} data-testid={`bundle-remove-${b.bundle_code}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {bundles.length > 0 && (
            <Card className="border-indigo-200" data-testid="scanned-usn-print-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <ScanBarcode className="w-5 h-5 text-indigo-600" /> Print USN Stickers
                </CardTitle>
                <p className="text-xs text-gray-500">Print the existing USN stickers on the scanned bundles</p>
              </CardHeader>
              <CardContent>
                <UsnStickerPrinter screenKey="inhouse-job-order"
                  groups={bundles.map((b) => ({ bundle_code: b.bundle_code, design_name: b.design_name, shade: b.shade, sort_no: b.sort_no, usns: b.usns || [] }))} />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="pt-4 space-y-3">
              <div>
                <Label className="text-xs text-gray-500">Remarks (optional)</Label>
                <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2}
                  placeholder="Any note for this job order…" className="mt-1" data-testid="remarks-input" />
              </div>
              <label className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50/60 p-3 cursor-pointer" data-testid="skip-pressing-label">
                <Checkbox checked={skipPressing} onCheckedChange={(v) => setSkipPressing(!!v)}
                  className="mt-0.5" data-testid="skip-pressing-checkbox" />
                <span className="text-sm text-amber-900">
                  <span className="font-medium">Skip pressing (jobber presses externally)</span>
                  <span className="block text-xs text-amber-700 mt-0.5">
                    When ticked, returned GOOD pieces are marked PRESSED and go straight to Assortment.
                    Leave unticked to route returned pieces through the in-house Pressing op.
                  </span>
                </span>
              </label>
              {!jobber && <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">Select a jobber</Badge>}
              {jobber && !design && <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">Select a target design</Badge>}
              {jobber && design && bundles.length === 0 && <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">Scan at least one bundle</Badge>}
              <Button className="w-full bg-indigo-700 hover:bg-indigo-800" disabled={!canSubmit} onClick={submit} data-testid="create-order-btn">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : `Raise Job Order — ${totalQty} pcs`}
              </Button>
            </CardContent>
          </Card>
        </div>
        <div className="md:col-span-2"><EwbDocPanel docType="job_work" section="Inhouse" title="Job order e-way bills" /></div>
      </main>
    </div>
  );
};

export default CreateJobOrder;
